import { onBeforeUnmount, onMounted, readonly, ref, type Ref } from 'vue'
import type { ComponentNode } from '@api/types.gen'

/**
 * The mechanic that decided the visitor is leaving. Written to
 * `var.global.exitIntentTrigger` by the page runtime and carried on the
 * `navigation:exit-intent` payload, so a pipe can branch its exit-intent
 * offer on how the visitor tried to leave.
 */
export type ExitIntentTrigger =
  | 'mouseleave'
  | 'tabSwitch'
  | 'pageLeave'
  | 'backNavigation'
  | 'timeOnPage'
  | 'scroll'

export const EXIT_INTENT_TRIGGER_VAR_KEY = 'var.global.exitIntentTrigger'

// Wallet payment popups - PayPal especially, whose button sits in a cross-origin
// iframe, so its "payment starting" signal reaches the page via async
// postMessage - can hide the tab a few ms before that signal sets suppression.
// The hidden-path trigger is only ever *seen* once the tab returns, so deciding
// a beat later costs nothing: defer the fire briefly to let a racing suppressor
// win, then re-check at fire() time. The desktop mouseleave path has no such
// async gap, so it is instant unless a pipe configures a dwell of its own.
//
// This was the hidden-dwell window itself until the dwell became configurable;
// it survives as the floor below which the race is lost, which the config
// editor warns about.
export const EXIT_INTENT_WALLET_RACE_MS = 300

// Legacy parity: pipelines/lib/common/js/components/exitIntentListener.js
// debounced its scroll handler by this much and fired at 90% of the document.
const SCROLL_DEBOUNCE_MS = 150

/**
 * Defaults reproduce the behaviour the detector had before any of it was
 * configurable, so an EXIT_INTENT integration row with an empty config is a
 * no-op. Every trigger a pipe has to opt into — tab switch, Back, timer,
 * scroll — is off here.
 *
 * `tabSwitchEnabled` is off because a hidden tab was dropped as a trigger
 * outright before this config existed: it fires on any app switch or
 * backgrounding, which is not exit intent. The path is kept and configurable,
 * but a pipe has to ask for it, and `tabSwitchHiddenMs` is the dwell it gets
 * when it does — five minutes, so the trigger means what its name says.
 */
export const EXIT_INTENT_DEFAULTS = {
  enabled: true,
  mouseEnabled: true,
  mouseTopThresholdPx: 10,
  mouseAwayMs: 0,
  tabSwitchEnabled: false,
  tabSwitchHiddenMs: 300_000,
  pageLeaveEnabled: true,
  backNavigation: 'off',
  backNavigationBlocks: true,
  timeOnPage: 'off',
  timeOnPageMs: 15_000,
  scrollEnabled: false,
  scrollRatio: 0.9,
  // What the pipe page hardcoded as `cooldownMs` before the config existed.
  armDelayMs: 1_000,
  triggerScope: 'page',
} as const

/**
 * Stored shape of the EXIT_INTENT integration's config (the API's
 * `ExitIntentIntegrationConfigDto`). Declared here rather than imported from
 * the integration so the page runtime can read the config without pulling an
 * optional integration into its bundle; the API DTO is what validates it.
 */
export interface ExitIntentConfig {
  enabled?: boolean
  mouseEnabled?: boolean
  mouseTopThresholdPx?: number
  mouseAwayMs?: number
  tabSwitchEnabled?: boolean
  tabSwitchHiddenMs?: number
  pageLeaveEnabled?: boolean
  backNavigation?: 'off' | 'exitIntentPages' | 'allPages'
  backNavigationBlocks?: boolean
  timeOnPage?: 'off' | 'elapsed' | 'inactivity'
  timeOnPageMs?: number
  scrollEnabled?: boolean
  scrollRatio?: number
  armDelayMs?: number
  triggerScope?: 'page' | 'session'
}

export interface ExitIntentOptions {
  /** Master switch — false disables every trigger, `reset()` included. */
  enabled?: boolean
  /**
   * Desktop flick detection: the cursor must exit at or above this Y
   * coordinate (in px) for the mouseleave path to fire. Filters out
   * bottom/side exits (dock, browser scrollbar) while catching the
   * "reach for tab bar / back button" signal.
   */
  cursorTopThreshold?: number
  /**
   * How long the cursor must stay outside the document after a top-edge exit
   * before it counts as leaving. A cursor that comes back inside within the
   * window cancels the pending fire — the visitor was reaching for a bookmark
   * or a notification, not going. Default 0 (fire on the exit itself).
   */
  cursorAwayMs?: number
  /** Disable the desktop mouseleave (top-edge) path. Default true. */
  enableMouseLeave?: boolean
  /**
   * The `document.visibilitychange → hidden` path. Off by default: a hidden
   * tab also means an app switch, a notification or an OS-chrome interaction,
   * none of which are exit intent. Opt in with a dwell long enough to mean
   * the visitor is actually gone.
   */
  enableVisibilityChange?: boolean
  /**
   * How long the tab must stay hidden before the switch counts as leaving.
   * A tab that comes back inside this window cancels the pending fire, and
   * the delay doubles as the race window an async payment-sheet suppressor
   * needs to win (see EXIT_INTENT_WALLET_RACE_MS).
   */
  visibilityHiddenMs?: number
  /**
   * The "this document is being left" (`pagehide`) path. Distinct from the
   * tab-switch one: it fires on a navigation out of the pipe, not on the tab
   * going to the background. Default true.
   */
  enablePageLeave?: boolean
  /**
   * Timer path. `elapsed` fires once the visitor has been on the page that
   * long; `inactivity` restarts the countdown on every interaction. Legacy
   * parity for `exitIntentTimerTrigger`, which was `elapsed`. Default 'off'.
   */
  timeOnPageMode?: 'off' | 'elapsed' | 'inactivity'
  /** Length of the timer path in ms. Default 15000 (legacy default). */
  timeOnPageMs?: number
  /**
   * Scroll-depth path (legacy `exitIntentScrollTrigger`). Default off. Only a
   * page the visitor has actually scrolled can reach the ratio, so a page that
   * fits the viewport never fires it.
   */
  enableScroll?: boolean
  /** Fraction of the document scrolled through that fires it. Default 0.9. */
  scrollRatio?: number
  /**
   * Short-circuit subsequent triggers until `reset()` is called. Default
   * true — matches standard exit-intent "once per session/page" UX.
   */
  once?: boolean
  /**
   * When false, `reset()` re-arms the cooldown and the timers but leaves a
   * consumed `once` token consumed, so exit intent fires at most once per
   * visit instead of once per page show. Default true.
   */
  retriggerPerPage?: boolean
  /**
   * Ignore triggers for this many ms after the detector is (re-)armed (on
   * mount and on every `reset()`). Default 0 (no cooldown). Used to absorb
   * the `visibilitychange → hidden` noise that mobile browsers emit around a
   * navigation gesture (soft-keyboard dismissal, app/tab switch, OS chrome) —
   * genuine exit intent happens seconds into a page, so a short window after
   * a page show should not count as the user leaving.
   */
  cooldownMs?: number
  /**
   * When this returns true at fire time, the trigger is skipped WITHOUT
   * consuming the `once` token, so exit-intent stays available for a later
   * genuine signal. Used to suppress EI while a component is mid-operation
   * (e.g. a GPay whose native sheet backgrounds the tab and looks exactly
   * like a real tab-switch, or a wallet redirect that leaves the document as
   * part of a legitimate payment rather than as an exit).
   */
  shouldSuppress?: () => boolean
  /**
   * Whether the view can act on exit intent at all right now — for the pipe
   * page, whether the page shown wires an `onExitIntent` handler. False skips
   * the trigger WITHOUT consuming the `once` token, exactly as
   * `shouldSuppress` does: a page with nothing to show must not spend a
   * `session`-scoped visit's one offer, and `request()` returning false is
   * what stops the Back trap cancelling a navigation nobody answered.
   *
   * It also gates `onTrigger`, so a detector fire that shows nothing is not
   * reported to analytics either. Default: always true.
   */
  canShow?: () => boolean
}

export interface ExitIntentHandle {
  triggered: Readonly<Ref<boolean>>
  /** The mechanic that fired, or null while exit intent has not fired. */
  trigger: Readonly<Ref<ExitIntentTrigger | null>>
  reset: () => void
  /**
   * Fire from a mechanic the detector cannot observe itself — today the
   * router-level Back trap in the pipe page. Runs the same once /
   * suppression / cooldown gate as the built-in paths and returns whether
   * exit intent actually fired, which is what lets the caller decide to
   * cancel the navigation that provoked it.
   */
  request: (trigger: ExitIntentTrigger) => boolean
}

const hasWiredHandler = (events: Record<string, unknown> | null | undefined): boolean => {
  const list = events?.onExitIntent
  return Array.isArray(list) && list.length > 0
}

const isPositiveNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0

/**
 * Turns a stored EXIT_INTENT config into detector options, filling every
 * absent or malformed value from EXIT_INTENT_DEFAULTS. Tolerant by design:
 * the config is stored JSON that can predate or postdate this build, and a
 * bad value must degrade to the pre-integration behaviour rather than
 * silently disable exit intent.
 */
export function resolveExitIntentOptions(
  config: ExitIntentConfig | null | undefined,
  overrides: Pick<ExitIntentOptions, 'shouldSuppress' | 'canShow'> = {},
): ExitIntentOptions {
  const c = config ?? {}
  return {
    enabled: c.enabled !== false,
    enableMouseLeave: c.mouseEnabled !== false,
    cursorTopThreshold: isPositiveNumber(c.mouseTopThresholdPx)
      ? c.mouseTopThresholdPx
      : EXIT_INTENT_DEFAULTS.mouseTopThresholdPx,
    cursorAwayMs: isPositiveNumber(c.mouseAwayMs)
      ? c.mouseAwayMs
      : EXIT_INTENT_DEFAULTS.mouseAwayMs,
    // Opt-in, unlike the paths above: only an explicit true arms it.
    enableVisibilityChange: c.tabSwitchEnabled === true,
    visibilityHiddenMs: isPositiveNumber(c.tabSwitchHiddenMs)
      ? c.tabSwitchHiddenMs
      : EXIT_INTENT_DEFAULTS.tabSwitchHiddenMs,
    enablePageLeave: c.pageLeaveEnabled !== false,
    timeOnPageMode:
      c.timeOnPage === 'elapsed' || c.timeOnPage === 'inactivity' ? c.timeOnPage : 'off',
    timeOnPageMs: isPositiveNumber(c.timeOnPageMs)
      ? c.timeOnPageMs
      : EXIT_INTENT_DEFAULTS.timeOnPageMs,
    cooldownMs: isPositiveNumber(c.armDelayMs) ? c.armDelayMs : EXIT_INTENT_DEFAULTS.armDelayMs,
    enableScroll: c.scrollEnabled === true,
    scrollRatio:
      isPositiveNumber(c.scrollRatio) && c.scrollRatio > 0 && c.scrollRatio <= 1
        ? c.scrollRatio
        : EXIT_INTENT_DEFAULTS.scrollRatio,
    retriggerPerPage: c.triggerScope !== 'session',
    ...overrides,
  }
}

/**
 * True when the Back trap should be armed for the page currently shown.
 * `exitIntentPages` is the safe mode to enable pipe-wide: only a page that
 * wires an onExitIntent handler has anything to show, so Back stays a plain
 * Back everywhere else.
 */
export function shouldTrapBackNavigation(
  config: ExitIntentConfig | null | undefined,
  pageHasExitIntentHandler: boolean,
): boolean {
  if (config?.enabled === false) return false
  if (config?.backNavigation === 'allPages') return true
  if (config?.backNavigation === 'exitIntentPages') return pageHasExitIntentHandler
  return false
}

/**
 * Whether the given page shows anything on exit intent — its own
 * `onExitIntent` bucket, or that of a shared node reachable from its tree
 * (where an exit-intent popup usually lives, since it is reused across
 * pages). Mirrors the editor-side coverage check in `pipeValidators.ts`.
 */
export function pageWiresExitIntent(
  pageEvents: Record<string, unknown> | null | undefined,
  children: ComponentNode[] | undefined,
  getSharedNodeById: (id: string) => { events?: Record<string, unknown> } | null,
): boolean {
  if (hasWiredHandler(pageEvents)) return true

  const walk = (nodes: ComponentNode[] | undefined, visited: Set<string>): boolean => {
    if (!nodes) return false
    for (const node of nodes) {
      if (hasWiredHandler(node.events)) return true
      if (node.type === 'SharedNodeRef') {
        const refId =
          typeof node.fields?.sharedNodeId === 'string' ? node.fields.sharedNodeId : null
        if (refId && !visited.has(refId)) {
          visited.add(refId)
          const sn = getSharedNodeById(refId)
          if (hasWiredHandler(sn?.events)) return true
          if (
            walk(
              (sn as { content?: { children?: ComponentNode[] } } | null)?.content?.children,
              visited,
            )
          ) {
            return true
          }
        }
      }
      if (node.children?.length && walk(node.children, visited)) return true
    }
    return false
  }

  return walk(children, new Set<string>())
}

/** Whether a trapped Back should be cancelled so the visitor stays put. */
export const shouldBlockBackNavigation = (config: ExitIntentConfig | null | undefined): boolean =>
  config?.backNavigationBlocks !== false

/**
 * Detects "the user is about to leave this view" via signals with a low
 * false-positive rate:
 *   - Desktop: cursor leaves the document near the top edge (tab bar / back
 *     button reach).
 *   - Any platform: `pagehide`, i.e. the document itself is being left.
 *   - Opt-in: tab-switching (`visibilitychange` → `hidden`, once the tab has
 *     stayed hidden long enough to mean the visitor is gone), a time-on-page
 *     / inactivity timer and a scroll-depth mark (both legacy-parity paths),
 *     plus `request()` for the page's Back trap.
 *
 * Listeners attach on mount, detach on unmount. Safe under SSR — all DOM
 * access is guarded by `typeof document !== 'undefined'`.
 */
export function useExitIntent(
  onTrigger: (trigger: ExitIntentTrigger) => void,
  options: ExitIntentOptions = {},
): ExitIntentHandle {
  const {
    enabled = true,
    cursorTopThreshold = EXIT_INTENT_DEFAULTS.mouseTopThresholdPx,
    cursorAwayMs = EXIT_INTENT_DEFAULTS.mouseAwayMs,
    enableMouseLeave = true,
    enableVisibilityChange = false,
    visibilityHiddenMs = EXIT_INTENT_DEFAULTS.tabSwitchHiddenMs,
    enablePageLeave = true,
    timeOnPageMode = 'off',
    timeOnPageMs = EXIT_INTENT_DEFAULTS.timeOnPageMs,
    enableScroll = false,
    scrollRatio = EXIT_INTENT_DEFAULTS.scrollRatio,
    once = true,
    retriggerPerPage = true,
    cooldownMs = 0,
    shouldSuppress,
    canShow,
  } = options

  const triggered = ref(false)
  const trigger = ref<ExitIntentTrigger | null>(null)
  // Timestamp of the last arm (mount / reset). Monotonic; `performance.now()`
  // avoids wall-clock skew and is SSR-safe (guarded below).
  let armedAt = typeof performance !== 'undefined' ? performance.now() : 0

  function fire(mechanic: ExitIntentTrigger): boolean {
    if (!enabled) return false
    if (once && triggered.value) return false
    // Skip WITHOUT consuming the `once` token, so EI stays available once the
    // suppressor releases (e.g. after a wallet payment finishes).
    if (shouldSuppress?.()) return false
    // Same rule for a view with nothing to show: burning the token there is
    // what made a `session`-scoped pipe fire once on its landing page and
    // never again on checkout.
    if (canShow && !canShow()) return false
    if (cooldownMs > 0 && typeof performance !== 'undefined') {
      if (performance.now() - armedAt < cooldownMs) return false
    }
    triggered.value = true
    trigger.value = mechanic
    onTrigger(mechanic)
    return true
  }

  let cursorAwayTimer: ReturnType<typeof setTimeout> | undefined
  let visibilityTimer: ReturnType<typeof setTimeout> | undefined
  let timeOnPageTimer: ReturnType<typeof setTimeout> | undefined
  let scrollDebounceTimer: ReturnType<typeof setTimeout> | undefined
  // When the tab was last hidden, and whether that dwell already reached its
  // decision — see onVisibilityChange. Starts settled: a page can boot in a
  // background tab (a link opened with ⌘-click) with no visibilitychange of
  // its own, and an unsettled start would count the whole pre-load history as
  // time away and fire the moment the visitor first looks at the page.
  let hiddenAt = 0
  let visibilityDwellSettled = true

  let lastActivityAt = Date.now()

  const onTimeOnPageDeadline = () => {
    // In `inactivity` mode the deadline is relative to the last interaction,
    // which moves. Re-arm for the remainder rather than resetting the timer on
    // every event — `scroll` alone would rebuild it dozens of times a second.
    if (timeOnPageMode === 'inactivity') {
      const idleFor = Date.now() - lastActivityAt
      if (idleFor < timeOnPageMs) {
        timeOnPageTimer = setTimeout(onTimeOnPageDeadline, timeOnPageMs - idleFor)
        return
      }
    }
    fire('timeOnPage')
  }

  const startTimeOnPageTimer = () => {
    if (timeOnPageTimer) clearTimeout(timeOnPageTimer)
    if (!enabled || timeOnPageMode === 'off') return
    lastActivityAt = Date.now()
    timeOnPageTimer = setTimeout(onTimeOnPageDeadline, timeOnPageMs)
  }

  function reset() {
    // A `session`-scoped detector keeps its consumed token across page shows;
    // the cooldown and the timers still re-arm so the next page starts clean.
    if (retriggerPerPage) {
      triggered.value = false
      trigger.value = null
    }
    // A signal that started on the page being left must not decide on the page
    // arriving: a debounced scroll check resolves against the NEW page's height
    // — smallest right after a navigation, before its images and payment frames
    // have sized — and a cursor dwell begun on the way out lands seconds into
    // the next step, where the offer reads as unprovoked. The hidden-tab dwell
    // is deliberately kept: it describes the tab, not the page, and a page can
    // be shown while the visitor is still away.
    if (cursorAwayTimer) clearTimeout(cursorAwayTimer)
    cursorAwayTimer = undefined
    if (scrollDebounceTimer) clearTimeout(scrollDebounceTimer)
    scrollDebounceTimer = undefined
    if (typeof performance !== 'undefined') armedAt = performance.now()
    startTimeOnPageTimer()
  }

  const onMouseLeave = (e: MouseEvent) => {
    if (e.clientY > cursorTopThreshold) return
    if (cursorAwayMs <= 0) {
      fire('mouseleave')
      return
    }
    if (cursorAwayTimer) clearTimeout(cursorAwayTimer)
    cursorAwayTimer = setTimeout(() => {
      cursorAwayTimer = undefined
      fire('mouseleave')
    }, cursorAwayMs)
  }

  // The cursor is back over the page before the dwell was up: the exit was a
  // reach for the tab bar or a bookmark the visitor did not take, so it is not
  // an exit. Re-entering anywhere counts — a return through the sides is still
  // a return.
  const onMouseReturn = () => {
    if (!cursorAwayTimer) return
    clearTimeout(cursorAwayTimer)
    cursorAwayTimer = undefined
  }

  // Defer by the hidden-dwell window (see EXIT_INTENT_WALLET_RACE_MS) so a
  // wallet's async suppression signal lands first; fire() still applies once /
  // shouldSuppress / cooldown when it runs.
  const onVisibilityChange = () => {
    if (document.visibilityState !== 'hidden') {
      // Tab returned before the dwell window elapsed. Cancel the pending fire
      // so a short round trip (e.g. a wallet sheet dismissed almost
      // immediately) doesn't pop EI right as the suppressor releases.
      if (visibilityTimer) clearTimeout(visibilityTimer)
      visibilityTimer = undefined
      // A backgrounded tab can be frozen outright — mobile Safari and Chrome
      // both do it — and a frozen tab runs no timers, so a long dwell window
      // would otherwise never be reached by the visitor it describes: away for
      // minutes, back to a page that never noticed. Decide it here instead,
      // but only when the timer never ran, or a fire the suppressor rightly
      // skipped would come back as a false positive on the visitor's return.
      // Wall clock rather than performance.now(): elapsed real time is the
      // question, and it is the one clock a freeze cannot stall.
      if (!visibilityDwellSettled && Date.now() - hiddenAt >= visibilityHiddenMs) {
        visibilityDwellSettled = true
        fire('tabSwitch')
      }
      return
    }

    if (visibilityTimer) clearTimeout(visibilityTimer)

    hiddenAt = Date.now()
    visibilityDwellSettled = false
    visibilityTimer = setTimeout(() => {
      visibilityDwellSettled = true
      fire('tabSwitch')
    }, visibilityHiddenMs)
  }

  // `pagehide` fires when the document is genuinely being LEFT: a back/forward
  // navigation out of the pipe, a link away, a reload, a tab close.
  //
  // `persisted` is the browser close/reopen guard: it is true when the page
  // went into the back/forward cache — a navigation the visitor can come back
  // from, so the offer is worth arming. A DISCARDED page (tab closed, reload)
  // reports false, and firing there would only ever be seen on a later
  // reopen, which isn't worth it.
  const onPageLeave = (e: PageTransitionEvent) => {
    if (!e.persisted) return
    fire('pageLeave')
  }

  const onScroll = () => {
    if (scrollDebounceTimer) clearTimeout(scrollDebounceTimer)
    scrollDebounceTimer = setTimeout(() => {
      // A page no taller than the viewport is already at its end the moment it
      // opens, so any scroll event at all — a mobile address bar collapsing,
      // the on-screen keyboard closing, the scroll-to-top every navigation
      // performs — would count as the visitor having read to the bottom of it.
      // Only a page they actually moved says anything about them.
      if (window.scrollY <= 0) return
      const reached = window.scrollY + window.innerHeight
      const height = document.documentElement.scrollHeight
      if (height > 0 && reached / height >= scrollRatio) fire('scroll')
    }, SCROLL_DEBOUNCE_MS)
  }

  // Any of these counts as the visitor still being here. Passive + capture so
  // a component stopping propagation cannot make them look idle.
  const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const
  const onActivity = () => {
    lastActivityAt = Date.now()
  }

  onMounted(() => {
    if (typeof document === 'undefined' || !enabled) return
    if (enableMouseLeave) {
      document.documentElement.addEventListener('mouseleave', onMouseLeave)
      if (cursorAwayMs > 0) {
        document.documentElement.addEventListener('mouseenter', onMouseReturn)
      }
    }
    if (enableVisibilityChange) document.addEventListener('visibilitychange', onVisibilityChange)
    if (enablePageLeave) window.addEventListener('pagehide', onPageLeave)
    if (enableScroll) window.addEventListener('scroll', onScroll, { passive: true })
    if (timeOnPageMode === 'inactivity') {
      ACTIVITY_EVENTS.forEach((name) =>
        document.addEventListener(name, onActivity, { passive: true, capture: true }),
      )
    }
    startTimeOnPageTimer()
  })

  onBeforeUnmount(() => {
    if (typeof document === 'undefined') return

    if (cursorAwayTimer) clearTimeout(cursorAwayTimer)
    if (visibilityTimer) clearTimeout(visibilityTimer)
    if (timeOnPageTimer) clearTimeout(timeOnPageTimer)
    if (scrollDebounceTimer) clearTimeout(scrollDebounceTimer)

    document.documentElement.removeEventListener('mouseleave', onMouseLeave)
    document.documentElement.removeEventListener('mouseenter', onMouseReturn)
    document.removeEventListener('visibilitychange', onVisibilityChange)
    window.removeEventListener('pagehide', onPageLeave)
    window.removeEventListener('scroll', onScroll)
    ACTIVITY_EVENTS.forEach((name) =>
      document.removeEventListener(name, onActivity, { capture: true }),
    )
  })

  return { triggered: readonly(triggered), trigger: readonly(trigger), reset, request: fire }
}
